/**
 * Welford's online algorithm for computing running statistics in O(1) memory.
 * No arrays needed — maintains mean, variance, min, max incrementally.
 */
export class RunningStats {
  private n = 0;
  private m = 0; // running mean
  private m2 = 0; // running sum of squared deviations
  private lo = Infinity;
  private hi = -Infinity;

  push(value: number): void {
    this.n++;
    const delta = value - this.m;
    this.m += delta / this.n;
    const delta2 = value - this.m;
    this.m2 += delta * delta2;

    if (value < this.lo) this.lo = value;
    if (value > this.hi) this.hi = value;
  }

  getMean(): number {
    return this.n === 0 ? 0 : this.m;
  }

  getStddev(): number {
    return this.n < 2 ? 0 : Math.sqrt(this.m2 / (this.n - 1));
  }

  getMin(): number {
    return this.n === 0 ? 0 : this.lo;
  }

  getMax(): number {
    return this.n === 0 ? 0 : this.hi;
  }

  getCount(): number {
    return this.n;
  }

  reset(): void {
    this.n = 0;
    this.m = 0;
    this.m2 = 0;
    this.lo = Infinity;
    this.hi = -Infinity;
  }

  toJSON(): { mean: number; stddev: number; min: number; max: number; count: number } {
    return {
      mean: this.getMean(),
      stddev: this.getStddev(),
      min: this.getMin(),
      max: this.getMax(),
      count: this.getCount(),
    };
  }
}
